import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { PencilIcon, Trash2 } from 'lucide-react';
import {
  AddButton,
  PageContainer,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  CloseButton,
  TealSwitch,
  EditButton,
  BulkImportLink,
  IconActionButton,
} from '../../Styles/AppStyles';
import {
  Form,
  FormSection,
  FormTextField,
} from '../../Components/Form';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { Header } from '../../Components/Header/Header';
import ConfirmationModal from '../../Components/Modal/ConfirmationModal';
import apiClient from '../../Services/ApiClient';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { useSnackbar } from '../../Context/SnackbarProvider';
import LocationPoPresetBulkImport from './LocationPoPresetBulkImport';

interface PoPreset {
  id: number;
  ovid: number;
  po_number: string;
  is_active: number;
  created_at: string;
}

const LocationPoPresetsPage: React.FC = () => {
  const { ovid } = useParams<{ ovid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const locationName: string = location.state?.locationName ?? `Location ${ovid}`;
  const { showSnackbar } = useSnackbar();

  const [presetsResponse, , , refetch] = useGetCustomData(`/api/location/${ovid}/po-presets`);
  const presetList: PoPreset[] = presetsResponse?.presets ?? [];

  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [addError, setAddError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [editTarget, setEditTarget] = useState<PoPreset | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<PoPreset | null>(null);

  const handleAdd = async () => {
    const trimmed = addValue.trim();
    if (!trimmed) { setAddError('PO/Job Number is required'); return; }
    if (trimmed.length > 200) { setAddError('Must be 200 characters or less'); return; }

    setIsSubmitting(true);
    try {
      const res = await apiClient(`/api/location/${ovid}/po-presets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json();
        setAddError(data.message ?? 'Failed to add');
        return;
      }
      showSnackbar('PO/Job Number added', 'success');
      setShowAddModal(false);
      setAddValue('');
      setAddError('');
      refetch();
    } catch {
      setAddError('Failed to add');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    const trimmed = editValue.trim();
    if (!trimmed) { setEditError('PO/Job Number is required'); return; }
    if (trimmed.length > 200) { setEditError('Must be 200 characters or less'); return; }

    setIsSubmitting(true);
    try {
      const res = await apiClient(`/api/location/${ovid}/po-presets/${editTarget.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: trimmed }),
      });
      if (!res.ok) {
        const data = await res.json();
        setEditError(data.message ?? 'Failed to update');
        return;
      }
      showSnackbar('PO/Job Number updated', 'success');
      setEditTarget(null);
      refetch();
    } catch {
      setEditError('Failed to update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (preset: PoPreset) => {
    try {
      const res = await apiClient(`/api/location/${ovid}/po-presets/${preset.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: preset.is_active ? 0 : 1 }),
      });
      if (!res.ok) {
        showSnackbar('Failed to update status', 'error');
        return;
      }
      showSnackbar(`PO/Job Number ${preset.is_active ? 'disabled' : 'enabled'}`, 'success');
      refetch();
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await apiClient(`/api/location/${ovid}/po-presets/${deleteTarget.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        showSnackbar('Failed to delete PO/Job Number', 'error');
        return;
      }
      showSnackbar('PO/Job Number deleted', 'success');
      setDeleteTarget(null);
      refetch();
    } catch {
      showSnackbar('Failed to delete PO/Job Number', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<PoPreset>[] = [
    { field: 'po_number', headerName: 'PO/Job Number', width: 300, sortable: true, searchable: true },
    { field: 'created_at', headerName: 'Created', width: 180, sortable: true },
    {
      field: 'id',
      headerName: 'Actions',
      width: 110,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <EditButton
            onClick={() => {
              setEditTarget(params.row);
              setEditValue(params.row.po_number);
              setEditError('');
            }}
            title="Edit"
          >
            <PencilIcon size={18} />
          </EditButton>
          <IconActionButton
            $variant="danger"
            onClick={() => setDeleteTarget(params.row)}
            title="Delete"
          >
            <Trash2 size={16} />
          </IconActionButton>
        </div>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <TealSwitch
          checked={!!params.row.is_active}
          onChange={() => handleToggleActive(params.row)}
        />
      ),
    },
  ];

  return (
    <PageContainer>
      <Header title={`PO/Job Presets — ${locationName}`} data={presetList} onBack={() => navigate(-1)}>
        <AddButton onClick={() => { setShowAddModal(true); setAddValue(''); setAddError(''); }}>+</AddButton>
      </Header>

      <BulkImportLink onClick={() => setShowImportModal(true)}>Bulk Import</BulkImportLink>

      <DataTable
        columns={columns}
        data={presetList}
        idField="id"
        pageKey="po-presets"
      />

      {showAddModal && (
        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            <Form
              embedded
              onSubmit={(e) => { e.preventDefault(); handleAdd(); }}
              title="Add PO/Job Number"
              subtitle="Enter a preset PO or job number for this location."
              submitLabel={isSubmitting ? 'Adding...' : 'Add'}
              onCancel={() => setShowAddModal(false)}
              isLoading={isSubmitting}
            >
              <FormSection>
                <FormTextField
                  label="PO/Job Number"
                  value={addValue}
                  onChange={(e) => { setAddValue(e.target.value); setAddError(''); }}
                  error={!!addError}
                  helperText={addError}
                  inputProps={{ maxLength: 200 }}
                  autoFocus
                />
              </FormSection>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {editTarget && (
        <ModalOverlay onClick={() => setEditTarget(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setEditTarget(null)}>×</CloseButton>
            <Form
              embedded
              onSubmit={(e) => { e.preventDefault(); handleEdit(); }}
              title="Edit PO/Job Number"
              submitLabel={isSubmitting ? 'Saving...' : 'Save'}
              onCancel={() => setEditTarget(null)}
              isLoading={isSubmitting}
            >
              <FormSection>
                <FormTextField
                  label="PO/Job Number"
                  value={editValue}
                  onChange={(e) => { setEditValue(e.target.value); setEditError(''); }}
                  error={!!editError}
                  helperText={editError}
                  inputProps={{ maxLength: 200 }}
                  autoFocus
                />
              </FormSection>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {showImportModal && ovid && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
            <ModalHeader>Bulk Import</ModalHeader>
            <LocationPoPresetBulkImport
              ovid={ovid}
              onClose={() => setShowImportModal(false)}
              onSuccess={() => refetch()}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete PO/Job Number?"
          message={`Are you sure you want to delete "${deleteTarget.po_number}"? This will remove it from the presets list for this location.`}
          confirmText="Delete"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
          isLoading={isDeleting}
          loadingText="Deleting..."
        />
      )}
    </PageContainer>
  );
};

export default LocationPoPresetsPage;
